/**
 * The artefact toolbar's glyphs, as inline SVG.
 *
 * They used to be emoji and bare characters (`🔗`, `•`, `1.`, `▦`, `⤴`, `﹌`). Three problems,
 * all of them visible in the bar: an emoji is a text glyph sitting on a font-decided baseline, so
 * centring its line box still leaves the drawing off-centre in the 30px square; its colour comes
 * from the emoji font, so it does not follow `.fmtbtn.on` or the disabled state; and it renders
 * as a different picture on every platform (`🔗` is a chain on Windows and a paperclip-ish loop
 * elsewhere). A viewBox has none of that — it IS the box, it strokes in `currentColor`, and it is
 * the same drawing everywhere.
 *
 * Shapes follow the Azure DevOps / Fluent vocabulary, which is what this audience already reads
 * in ADO all day: a chain for link, a broken chain for unlink, dots-and-rules for the lists, a
 * framed picture for image, connected boxes for a diagram.
 *
 * Every icon is decorative — the button around it owns the accessible name (an aria-label for the
 * icon-only controls in the bar, the visible text for the labelled ones in the overflow menu).
 * Sizing and colour come from `.fmt-ico` in styles.css.
 */

interface IconProps {
  /** Extra class on the <svg>, e.g. to size one icon differently. */
  className?: string
}

const cls = (extra?: string): string => 'fmt-ico' + (extra ? ' ' + extra : '')

/** A filled dot — the class sets `fill:none`, so the bullets have to opt back in. */
const DOT = { fill: 'currentColor', stroke: 'none' } as const
/** Same opt-out for the glyphs drawn as type rather than as strokes (the S, the list numbers). */
const TYPE = { fill: 'currentColor', stroke: 'none', fontWeight: 700 } as const

export function IconLink({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </svg>
  )
}

/**
 * Unlink — the same chain, badged with a dismiss ✕ (Fluent's `LinkDismiss` shape).
 *
 * A *broken* chain was the first draft and it failed the only test that matters here: at 16px,
 * sitting immediately next to Link, a gap of a pixel or two is not a difference anyone sees. The
 * badge is. The chain is the identical path scaled to 0.8 to clear the corner — `strokeWidth`
 * is bumped to 2.25 on the group so that 0.8 lands back on the 1.8 every other icon strokes at.
 */
export function IconUnlink({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g transform="scale(0.8)" strokeWidth="2.25">
        <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
        <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
      </g>
      <path d="M16.5 16.5 22 22M22 16.5l-5.5 5.5" />
    </svg>
  )
}

/** Anchor (AB#350) — a ship's anchor: ring, shank, stock and the two flukes. */
export function IconAnchor({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="5" r="2.2" />
      <path d="M12 7.2V21M8 10.5h8" />
      <path d="M4.5 13.5a7.5 7.5 0 0 0 15 0" />
      <path d="M4.5 13.5 3 15.5M19.5 13.5l1.5 2" />
    </svg>
  )
}

export function IconBulletList({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="4.5" cy="7" r="1.3" {...DOT} />
      <circle cx="4.5" cy="12" r="1.3" {...DOT} />
      <circle cx="4.5" cy="17" r="1.3" {...DOT} />
      <line x1="9" y1="7" x2="20.5" y2="7" />
      <line x1="9" y1="12" x2="20.5" y2="12" />
      <line x1="9" y1="17" x2="20.5" y2="17" />
    </svg>
  )
}

export function IconNumberedList({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {/* Set as type, like the product initials in FileIcons.tsx: stroking three legible digits
          at 16px costs far more path than it is worth. */}
      <text x="1.5" y="9.4" fontSize="7" {...TYPE}>1</text>
      <text x="1.5" y="14.4" fontSize="7" {...TYPE}>2</text>
      <text x="1.5" y="19.4" fontSize="7" {...TYPE}>3</text>
      <line x1="9" y1="7" x2="20.5" y2="7" />
      <line x1="9" y1="12" x2="20.5" y2="12" />
      <line x1="9" y1="17" x2="20.5" y2="17" />
    </svg>
  )
}

export function IconTable({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="4.5" width="18" height="15" rx="1.5" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <line x1="3" y1="14.5" x2="21" y2="14.5" />
      <line x1="9" y1="4.5" x2="9" y2="19.5" />
      <line x1="15" y1="4.5" x2="15" y2="19.5" />
    </svg>
  )
}

export function IconImage({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <circle cx="8.2" cy="9.5" r="1.4" />
      <path d="M21 15.5 16.5 11 5.5 19.5" />
    </svg>
  )
}

/** Mermaid — a generic flowchart: one node branching into two. */
export function IconFlowchart({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="8.5" y="2.5" width="7" height="5" rx="1" />
      <rect x="2" y="16.5" width="7" height="5" rx="1" />
      <rect x="15" y="16.5" width="7" height="5" rx="1" />
      <path d="M12 7.5V14" />
      <path d="M5.5 16.5V14h13v2.5" />
    </svg>
  )
}

/** "Generate an SVG with AI" — a picture frame with the sparkle every AI affordance here carries. */
export function IconAiImage({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="2.5" y="7" width="13.5" height="12.5" rx="2" />
      <circle cx="6.6" cy="11.4" r="1.2" />
      <path d="M16 16.5 12 12.5 4 19.5" />
      <path d="M19 3.5l1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1z" />
    </svg>
  )
}

export function IconUndo({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 10h10.5a5 5 0 0 1 0 10H8" />
      <path d="M8 6 4 10l4 4" />
    </svg>
  )
}

export function IconRedo({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 10H9.5a5 5 0 0 0 0 10H16" />
      <path d="M16 6l4 4-4 4" />
    </svg>
  )
}

export function IconStrike({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <text x="12" y="17.5" textAnchor="middle" fontSize="15" {...TYPE}>S</text>
      <line x1="3.5" y1="12" x2="20.5" y2="12" />
    </svg>
  )
}

/** A highlighter nib over a highlighted bar — the "surlignage" control. */
export function IconHighlight({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M14.5 4.5l5 5-7 7H7.5v-5z" />
      <line x1="10.5" y1="8.5" x2="15.5" y2="13.5" />
      <line x1="3.5" y1="20.5" x2="20.5" y2="20.5" strokeWidth="3" />
    </svg>
  )
}

export function IconQuote({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="4" y1="5" x2="4" y2="19" strokeWidth="2.6" />
      <line x1="9" y1="8" x2="20.5" y2="8" />
      <line x1="9" y1="12" x2="20.5" y2="12" />
      <line x1="9" y1="16" x2="16" y2="16" />
    </svg>
  )
}

export function IconCode({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8.5 8 4 12l4.5 4" />
      <path d="M15.5 8 20 12l-4.5 4" />
      <line x1="13.2" y1="6" x2="10.8" y2="18" />
    </svg>
  )
}

/** A rule set between two blocks of text — the `---` of markdown. */
export function IconRule({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="3" y1="12" x2="21" y2="12" strokeWidth="2.8" />
      <line x1="7" y1="6.5" x2="17" y2="6.5" strokeWidth="1.3" opacity=".4" />
      <line x1="7" y1="17.5" x2="17" y2="17.5" strokeWidth="1.3" opacity=".4" />
    </svg>
  )
}

/** The overflow "…" — the controls that did not earn a slot in the bar. */
export function IconMore({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="5.5" cy="12" r="1.6" {...DOT} />
      <circle cx="12" cy="12" r="1.6" {...DOT} />
      <circle cx="18.5" cy="12" r="1.6" {...DOT} />
    </svg>
  )
}

/** A window with its left pane marked — shows / hides a side panel (the slides' thumbnails). */
export function IconSidePanel({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <line x1="9.5" y1="4.5" x2="9.5" y2="19.5" />
    </svg>
  )
}

/** A magnifier — Find / Replace (AB#330), the same glyph at the right end of the prose and the
 *  slides bars. */
export function IconSearch({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10 10l4.5 4.5" />
    </svg>
  )
}

/** A paint roller — the format painter ("Copy style"), the same glyph in the slides (AB#323) and
 *  the prose (AB#331) bars. */
export function IconPaintFormat({ className }: IconProps = {}) {
  return (
    <svg className={cls(className)} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="2" y="1.5" width="10" height="4" rx="0.5" />
      <path d="M12 3.5h2v4H8v2" />
      <rect x="6.5" y="9.5" width="3" height="5" rx="0.5" />
    </svg>
  )
}
